import { PortalMaterialOrderRO } from '@escapenavigator/types/dist/partners-portal/portal-material-order.ro';
import {
    Badge,
    Button,
    Card,
    Center,
    CopyButton,
    Group,
    Loader,
    Modal,
    NumberInput,
    Stack,
    Table,
    Text,
    Textarea,
    Title,
    Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconCopy, IconPackage, IconQrcode } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import { api } from '../api';
import { apiErrorMessage } from '../api/client';
import { PageHeader } from '../components/page-header';
import { MaterialStatusBadge } from '../components/status-badge';
import { t } from '../dictionary';
import { useAuth } from '../providers/auth-context';

export const MaterialsPage = () => {
    const { partner } = useAuth();
    const [orders, setOrders] = useState<PortalMaterialOrderRO[] | null>(null);
    const [opened, { open, close }] = useDisclosure(false);

    const [quantity, setQuantity] = useState<number | string>(100);
    const [address, setAddress] = useState('');
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState(false);

    const load = () => {
        api.materialOrders()
            .then(({ data }) => setOrders(data))
            .catch(() => setOrders([]));
    };

    useEffect(load, []);

    const submit = async () => {
        if (!address.trim()) {
            notifications.show({ color: 'red', message: 'Enter a delivery address' });

            return;
        }

        setSaving(true);

        try {
            await api.createMaterialOrder({
                quantity: +quantity || 100,
                address,
                comment: comment || undefined,
            });

            notifications.show({ color: 'green', message: t.materials.ordered });
            close();
            setAddress('');
            setComment('');
            load();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    if (!orders) {
        return (
            <Center h={300}>
                <Loader />
            </Center>
        );
    }

    return (
        <>
            <PageHeader
                title={t.materials.title}
                subtitle={t.materials.subtitle}
                action={
                    <Button leftSection={<IconPackage size={16} />} onClick={open}>
                        {t.materials.orderButton}
                    </Button>
                }
            />

            <Stack gap='lg'>
                <Card padding='lg'>
                    <Group justify='space-between' wrap='wrap'>
                        <Group gap='md'>
                            <IconQrcode
                                size={36}
                                stroke={1.4}
                                color='var(--mantine-color-indigo-6)'
                            />
                            <div>
                                <Text fw={650}>{t.materials.promocodeCard}</Text>
                                <Text size='sm' c='dimmed' maw={480}>
                                    {t.materials.promocodeText}
                                </Text>
                            </div>
                        </Group>
                        <Group gap='xs'>
                            <Badge size='xl' variant='light' radius='md' ff='monospace'>
                                {partner.promocode ?? '—'}
                            </Badge>
                            {partner.promocode && (
                                <CopyButton value={partner.promocode}>
                                    {({ copied, copy }) => (
                                        <Tooltip label={copied ? 'Copied' : 'Copy'}>
                                            <Button
                                                variant='subtle'
                                                size='compact-sm'
                                                onClick={copy}
                                                leftSection={
                                                    copied ? (
                                                        <IconCheck size={14} />
                                                    ) : (
                                                        <IconCopy size={14} />
                                                    )
                                                }
                                            >
                                                {copied ? 'Copied' : 'Copy'}
                                            </Button>
                                        </Tooltip>
                                    )}
                                </CopyButton>
                            )}
                        </Group>
                    </Group>
                </Card>

                <Card padding='lg'>
                    <Text fw={650} mb='sm'>
                        {t.materials.history}
                    </Text>

                    {!orders.length ? (
                        <Center py='xl'>
                            <Stack align='center' gap={4} maw={380}>
                                <Title order={5}>{t.materials.empty}</Title>
                                <Text size='sm' c='dimmed' ta='center'>
                                    {t.materials.emptyText}
                                </Text>
                            </Stack>
                        </Center>
                    ) : (
                        <Table verticalSpacing='sm'>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>#</Table.Th>
                                    <Table.Th>{t.materials.quantity}</Table.Th>
                                    <Table.Th>{t.materials.address}</Table.Th>
                                    <Table.Th>{t.materials.tracking}</Table.Th>
                                    <Table.Th />
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {orders.map((order) => (
                                    <Table.Tr key={order.id}>
                                        <Table.Td>
                                            {new Date(order.createdAt).toLocaleDateString('en')}
                                        </Table.Td>
                                        <Table.Td>{order.quantity}</Table.Td>
                                        <Table.Td>{order.address}</Table.Td>
                                        <Table.Td>{order.trackingInfo ?? '—'}</Table.Td>
                                        <Table.Td>
                                            <MaterialStatusBadge status={order.status} />
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    )}
                </Card>
            </Stack>

            <Modal opened={opened} onClose={close} title={t.materials.orderButton} centered>
                <Stack gap='md'>
                    <NumberInput
                        label={t.materials.quantity}
                        min={50}
                        max={5000}
                        step={50}
                        value={quantity}
                        onChange={(v) => setQuantity(typeof v === 'bigint' ? Number(v) : v)}
                    />
                    <Textarea
                        label={t.materials.address}
                        placeholder={t.materials.addressPlaceholder}
                        minRows={2}
                        autosize
                        value={address}
                        onChange={(e) => setAddress(e.currentTarget.value)}
                    />
                    <Textarea
                        label={t.materials.comment}
                        placeholder={t.materials.commentPlaceholder}
                        value={comment}
                        onChange={(e) => setComment(e.currentTarget.value)}
                    />
                    <Group justify='flex-end'>
                        <Button variant='default' onClick={close}>
                            {t.common.cancel}
                        </Button>
                        <Button loading={saving} onClick={submit}>
                            {t.materials.orderButton}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
};
